import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useWorkshopBodyshop, useWorkshopPickDrop } from "../../lib/api";
import { useAnalyticsScope, ALL } from "../../lib/useAnalyticsScope";
import { bodyshopCsv } from "../../lib/msdAnalytics";
import { AN_CARD } from "../../components/msd/analytics";
import { AnalyticsHead } from "../../components/msd/AnalyticsHead";
import { MsdPage } from "../../components/msd/Shell";
import { BodyshopCard, downloadBodyshopCsv } from "@packs/automotive/web/components/msd/BodyshopCard";
import { ServiceLoadCard, downloadServiceLoadCsv } from "../../components/msd/ServiceLoadCard";

const TABS = [
  ["pd", "Service load vs P&D"],
  ["bs", "BS Intake vs JC Opening"],
];

// Service analytics: the business numbers behind the job cards, one view per
// tab. Each tab only asks the server for its own figures.
export default function ServiceAnalytics() {
  const nav = useNavigate();
  const scope = useAnalyticsScope();
  const { base, range } = scope;
  const [tab, setTab] = useState("pd");
  const pickDrop = useWorkshopPickDrop(tab === "pd" ? base : null);
  const bodyshop = useWorkshopBodyshop(tab === "bs" ? base : null);
  const workshop = (name) => String(name || "").replace(/^Maruti /, "");
  const locName = scope.loc === ALL ? "All locations" : scope.loc;
  const stamp = `${format(range.from, "yyyy-MM-dd")}_${format(range.to, "yyyy-MM-dd")}`;

  if (!scope.allowed) {
    return (
      <div className={AN_CARD} data-testid="analytics-no-access">
        <p className="text-[15px] text-msd-muted">Service analytics isn&apos;t available for your role.</p>
      </div>
    );
  }

  const active = tab === "pd" ? pickDrop : bodyshop;

  return (
    <MsdPage className="flex flex-col gap-6 [&>*]:shrink-0" data-testid="service-analytics">
        <AnalyticsHead
          scope={scope}
          divider={false}
          title="Service analytics"
          description="The business numbers behind the job cards."
        />
        <div
          role="tablist"
          aria-label="Service analytics views"
          className="-mt-2 flex max-w-full gap-6 overflow-x-auto border-b border-msd-line"
        >
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              data-testid={`service-tab-${key}`}
              onClick={() => setTab(key)}
              className={`-mb-px whitespace-nowrap border-0 border-b-2 border-solid bg-transparent px-0.5 pb-3 pt-1 text-[14px] ${
                tab === key ? "border-msd-royal font-semibold text-msd-ink" : "border-transparent font-medium text-msd-muted hover:text-msd-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {active.isError ? (
          <div className={`${AN_CARD} text-center`}>
            <div className="text-[20px] font-semibold">Couldn&apos;t load the numbers</div>
          </div>
        ) : !active.data ? null : tab === "pd" ? (
          <ServiceLoadCard
            data={active.data}
            scopeLabel={locName}
            workshopLabel={workshop}
            onExport={() => downloadServiceLoadCsv(active.data, `service-load-vs-pick-and-drop_${stamp}.csv`)}
          />
        ) : (
          <BodyshopCard
            data={active.data}
            scopeLabel={locName}
            workshopLabel={workshop}
            onOpenGroup={(w) => nav("/desk", { state: { openRow: w.group_row_id } })}
            onExport={() => downloadBodyshopCsv(bodyshopCsv(active.data), `bs-intake-vs-jc-opening_${stamp}.csv`)}
          />
        )}
    </MsdPage>
  );
}
